<template>
    <div class="report-query-container">
        <!-- 查询条件区域 -->
        <el-card class="query-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <span class="header-title">vivo广告报表查询</span>
                </div>
            </template>

            <el-form :model="queryParams" ref="queryForm" :inline="true" class="query-form">
                <el-form-item label="账户名称" prop="accountName">
                    <el-input type="textarea" v-model="queryParams.accountName" style="width: 240px;"
                        placeholder="每行一个账户名称" :rows="4">
                    </el-input>
                </el-form-item>

                <el-form-item label="区域" prop="area">
                    <el-select v-model="queryParams.area" placeholder="请选择区域" style="width: 150px">
                        <el-option v-for="item in regionOptions" :key="item.value" :label="item.label"
                            :value="item.value" />
                    </el-select>
                </el-form-item>

                <el-form-item label="时间范围" prop="timeRange">
                    <el-date-picker v-model="queryParams.timeRange" type="daterange" range-separator="至"
                        start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd"
                        format="yyyy-MM-dd" style="width: 400px" />
                    <div class="shortcut-group">
                        <span class="shortcut-label">快捷选择</span>
                        <el-button size="mini" :type="activeShortcut === 'today' ? 'primary' : ''"
                            @click="setTimeShortcut('today')">今日</el-button>
                        <el-button size="mini" :type="activeShortcut === 'yesterday' ? 'primary' : ''"
                            @click="setTimeShortcut('yesterday')">昨日</el-button>
                        <el-button size="mini" :type="activeShortcut === 'last4days' ? 'primary' : ''"
                            @click="setTimeShortcut('last4days')">最近四天</el-button>
                        <el-button size="mini" :type="activeShortcut === 'last7days' ? 'primary' : ''"
                            @click="setTimeShortcut('last7days')">最近七天</el-button>
                    </div>
                </el-form-item>

                <el-form-item label="转化指标" prop="conversionType">
                    <el-select v-model="queryParams.conversionType" placeholder="请选择转化指标" style="width: 200px">
                        <el-option label="加桌数" value="addDesktopC" />
                        <el-option label="首日ROI-广告变现" value="firstDayRoiAdMonetizationC" />
                        <el-option label="自定义激活数" value="backActivateCount" />
                    </el-select>
                </el-form-item>

                <el-form-item class="form-actions">
                    <el-button type="primary" @click="handleQuery" icon="el-icon-search">
                        查询
                    </el-button>
                    <el-button @click="handleReset" icon="el-icon-refresh">
                        重置
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 查询结果区域 -->
        <el-card class="result-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <span class="header-title">查询结果</span>
                    <div>
                        <el-button type="primary" icon="el-icon-download" @click="handleExport"
                            :disabled="!allTableData.length">
                            导出Excel
                        </el-button>
                    </div>
                </div>
            </template>

            <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%">
                <el-table-column prop="accountName" label="账户名" min-width="120" fixed="left"
                    show-overflow-tooltip />
                <el-table-column prop="uuid" label="uuid" min-width="200" fixed="left" show-overflow-tooltip />

                <template v-for="day in dateList">
                    <el-table-column :key="day + '_spent'" :label="day + '消耗'" width="120" align="center">
                        <template slot-scope="scope">
                            <span>{{ formatSpent(scope.row[day + '消耗']) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column :key="day + '_conversion'" :label="day + '转化'" width="120" align="center">
                        <template slot-scope="scope">
                            <span>{{ formatConversion(scope.row[day + '转化']) }}</span>
                        </template>
                    </el-table-column>
                </template>
            </el-table>

            <!-- 分页组件 -->
            <el-pagination class="pagination" @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper" :total="pagination.total" />
        </el-card>
    </div>
</template>

<script>
import dayjs from 'dayjs'
import { post } from '@/until/controlRequest'
import { exportJsonToExcel } from '@/until/export2Excel'

export default {
    name: 'VivoAdReportQuery',
    data() {
        return {
            regionOptions: [
                { label: '浙江', value: '1' },
                { label: '福建', value: '2' }
            ],
            queryParams: {
                accountName: '',
                area: '',
                timeRange: [],
                conversionType: 'addDesktopC'
            },
            allTableData: [],
            tableData: [],
            loading: false,
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },
            activeShortcut: '',
            startDate: '',
            endDate: '',
            dateList: []
        }
    },
    methods: {
        handleQuery() {
            if (!this.queryParams.accountName || !this.queryParams.accountName.trim()) {
                this.$message.error('请输入账户名称')
                return
            }
            if (!this.queryParams.area) {
                this.$message.error('请选择区域')
                return
            }
            if (!this.queryParams.timeRange || this.queryParams.timeRange.length !== 2) {
                this.$message.error('请选择时间范围')
                return
            }
            const [startTime, endTime] = this.queryParams.timeRange
            if (new Date(startTime) > new Date(endTime)) {
                this.$message.error('开始时间不能大于结束时间')
                return
            }
            if (!this.queryParams.conversionType) {
                this.$message.error('请选择转化指标')
                return
            }
            this.fetchReport()
        },

        async fetchReport() {
            const textList = this.queryParams.accountName.split('\n')
                .map(s => s.trim())
                .filter(s => s !== '')
                .filter((item, index, array) => array.indexOf(item) === index)

            const [startTime, endTime] = this.queryParams.timeRange
            const requestData = {
                textList,
                area: this.queryParams.area,
                startDate: startTime,
                endDate: endTime,
                conversionType: this.queryParams.conversionType
            }

            this.loading = true
            try {
                const data = await post('/v1/vivo/report/query', requestData)
                this.startDate = startTime
                this.endDate = endTime
                this.dateList = this.buildDateList(startTime, endTime)
                this.allTableData = this.pivotData(data || [], this.dateList)
                this.pagination.total = this.allTableData.length
                this.pagination.currentPage = 1
                this.updatePageData()
                if (!this.allTableData.length) {
                    this.$message.info('没有查询到数据')
                }
            } catch (e) {
                console.error('查询失败:', e)
                this.$message.error('查询失败: ' + (e.message || '未知错误'))
            } finally {
                this.loading = false
            }
        },

        // 生成 start ~ end（含两端）的 yyyyMMdd 日期列表
        buildDateList(start, end) {
            const list = []
            let cur = dayjs(start)
            const endDay = dayjs(end)
            while (cur.isBefore(endDay) || cur.isSame(endDay)) {
                list.push(cur.format('YYYYMMDD'))
                cur = cur.add(1, 'day')
            }
            return list
        },

        // 后端原始按天数据 -> 账户为行、日期展开为列的表格数据
        pivotData(list, dateList) {
            return list.map(item => {
                const row = { accountName: item.accountName, uuid: item.uuid }
                const dayMap = {}
                ;(item.list || []).forEach(d => {
                    dayMap[d.date] = { spent: d.spent, conversion: d.conversion }
                })
                dateList.forEach(day => {
                    row[day + '消耗'] = dayMap[day] ? dayMap[day].spent : 0
                    row[day + '转化'] = dayMap[day] ? dayMap[day].conversion : 0
                })
                return row
            })
        },

        formatSpent(val) {
            if (val === undefined || val === null || val === '') return '-'
            return Number(val).toFixed(2)
        },

        formatConversion(val) {
            if (val === undefined || val === null || val === '') return '-'
            const num = Number(val)
            if (this.queryParams.conversionType === 'firstDayRoiAdMonetizationC') {
                return (num * 100).toFixed(2) + '%'
            }
            return Math.round(num)
        },

        updatePageData() {
            const { currentPage, pageSize } = this.pagination
            const start = (currentPage - 1) * pageSize
            this.tableData = this.allTableData.slice(start, start + pageSize)
        },

        handleSizeChange(size) {
            this.pagination.pageSize = size
            this.pagination.currentPage = 1
            this.updatePageData()
        },

        handleCurrentChange(page) {
            this.pagination.currentPage = page
            this.updatePageData()
        },

        setTimeShortcut(type) {
            this.activeShortcut = type
            const today = new Date()
            const formatDate = (d) => {
                const year = d.getFullYear()
                const month = String(d.getMonth() + 1).padStart(2, '0')
                const day = String(d.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
            }

            let start, end
            switch (type) {
                case 'today':
                    start = formatDate(today)
                    end = formatDate(today)
                    break
                case 'yesterday': {
                    const yesterday = new Date(today)
                    yesterday.setDate(today.getDate() - 1)
                    start = formatDate(yesterday)
                    end = formatDate(yesterday)
                    break
                }
                case 'last4days': {
                    const last4 = new Date(today)
                    last4.setDate(today.getDate() - 3)
                    start = formatDate(last4)
                    end = formatDate(today)
                    break
                }
                case 'last7days': {
                    const last7 = new Date(today)
                    last7.setDate(today.getDate() - 6)
                    start = formatDate(last7)
                    end = formatDate(today)
                    break
                }
                default:
                    return
            }
            this.queryParams.timeRange = [start, end]
        },

        handleReset() {
            this.queryParams = {
                accountName: '',
                area: '',
                timeRange: [],
                conversionType: 'addDesktopC'
            }
            this.activeShortcut = ''
            this.allTableData = []
            this.tableData = []
            this.dateList = []
            this.pagination.total = 0
            this.pagination.currentPage = 1
        },

        handleExport() {
            if (!this.allTableData.length) {
                this.$message.warning('没有数据可导出')
                return
            }

            const dateKeys = this.dateList.reduce((acc, day) => {
                acc.push(day + '消耗', day + '转化')
                return acc
            }, [])
            const header = ['账户名', 'uuid', ...dateKeys]
            const key = ['accountName', 'uuid', ...dateKeys]

            // 导出值按展示格式格式化
            const exportData = this.allTableData.map(row => {
                const obj = { accountName: row.accountName, uuid: row.uuid }
                this.dateList.forEach(day => {
                    obj[day + '消耗'] = this.formatSpent(row[day + '消耗'])
                    obj[day + '转化'] = this.formatConversion(row[day + '转化'])
                })
                return obj
            })

            exportJsonToExcel({
                header,
                data: exportData,
                key,
                filename: `vivo广告报表_${this.startDate}_${this.endDate}`
            })
        }
    }
}
</script>

<style scoped>
.report-query-container {
    padding: 20px;
    background-color: #f7f8fa;
    min-height: calc(100vh - 60px);
    box-sizing: border-box;
}

.query-card,
.result-card {
    border: 1px solid #e4e7ed;
    border-radius: 8px;
}

.query-card {
    margin-bottom: 16px;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    color: #303133;
}

.header-title::before {
    content: '';
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background: #409eff;
    margin-right: 8px;
}

.query-form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
}

.query-form .el-form-item {
    margin-right: 24px;
    margin-bottom: 12px;
}

.query-form .form-actions {
    margin-right: 0;
}

.shortcut-group {
    margin-top: 6px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.shortcut-label {
    font-size: 12px;
    color: #909399;
    margin-right: 4px;
}

.pagination {
    margin-top: 16px;
    text-align: right;
}

.result-card ::v-deep .el-table th {
    background-color: #f5f7fa;
    color: #303133;
    font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/vivo/vivoAdReportQuery.vue